import * as XLSX from 'xlsx';

/**
 * Export customer data to Excel
 */
export const exportCustomerToExcel = (customer: any, pods: any[]) => {
  // Create workbook
  const wb = XLSX.utils.book_new();

  // Sheet 1: Customer basic info
  const customerData = [
    ['Ügyfél adatok'],
    [''],
    ['Cégnév', customer.companyName || customer.name || ''],
    ['Adószám', customer.taxNumber || ''],
    ['Kapcsolattartó', customer.contactPerson || ''],
    ['Email', customer.email || ''],
    ['Telefon', customer.phone || ''],
    [''],
    ['Cím'],
    ['Ország', customer.address?.country || ''],
    ['Irányítószám', customer.address?.postalCode || ''],
    ['Város', customer.address?.city || ''],
    ['Utca', customer.address?.street || ''],
    [''],
    ['Számlázási beállítások'],
    ['Számlázási mód', customer.bulkInvoicingSettings?.invoicingMode || 'per_pod'],
    ['Pénznem', customer.bulkInvoicingSettings?.currency || 'HUF'],
    ['Fizetési határidő (nap)', customer.bulkInvoicingSettings?.paymentDeadline || 15],
    [''],
    ['Aktuális árazás'],
    ['Energiadíj (Ft/kWh)', customer.currentPricing?.price || 0],
    ['Érvényesség kezdete', customer.currentPricing?.validFrom || ''],
    ['Érvényesség vége', customer.currentPricing?.validTo || ''],
  ];

  const ws1 = XLSX.utils.aoa_to_sheet(customerData);
  
  // Set column widths
  ws1['!cols'] = [
    { wch: 30 },
    { wch: 40 }
  ];

  XLSX.utils.book_append_sheet(wb, ws1, 'Ügyfél adatok');

  // Sheet 2: PODs
  if (pods && pods.length > 0) {
    const podHeaders = [
      'POD Azonosító',
      'Cím',
      'Irányítószám',
      'Város',
      'Utca, házszám',
      'Típus',
      'Státusz',
      'Fogyasztási kategória',
      'Mérőóra típus',
      'Mérőóra szám',
      'Szerződés kezdete',
      'Szerződés vége',
      'Megjegyzés'
    ];

    const podData = pods.map(pod => [
      pod.id || '',
      pod.address || '',
      pod.postalCode || '',
      pod.city || '',
      pod.street || '',
      pod.type || '',
      pod.status || '',
      pod.consumptionCategory || '',
      pod.meterType || '',
      pod.meterNumber || '',
      pod.contractStartDate || '',
      pod.contractEndDate || '',
      pod.notes || ''
    ]);

    const ws2 = XLSX.utils.aoa_to_sheet([podHeaders, ...podData]);
    
    // Set column widths
    ws2['!cols'] = [
      { wch: 35 }, // POD ID
      { wch: 40 }, // Address
      { wch: 15 }, // Postal Code
      { wch: 20 }, // City
      { wch: 30 }, // Street
      { wch: 15 }, // Type
      { wch: 15 }, // Status
      { wch: 20 }, // Category
      { wch: 20 }, // Meter type
      { wch: 20 }, // Meter number
      { wch: 15 }, // Start date
      { wch: 15 }, // End date
      { wch: 30 }  // Notes
    ];

    XLSX.utils.book_append_sheet(wb, ws2, 'POD-ok');
  }

  // Sheet 3: Invoice groups (if any)
  if (customer.invoiceGroups && customer.invoiceGroups.length > 0) {
    const groupHeaders = [
      'Csoport neve',
      'POD-ok száma',
      'POD azonosítók',
      'Létrehozva'
    ];

    const groupData = customer.invoiceGroups.map((group: any) => [
      group.name || '',
      group.podIds?.length || 0,
      group.podIds?.join(', ') || '',
      group.createdAt?.toDate?.()?.toLocaleDateString('hu-HU') || ''
    ]);

    const ws3 = XLSX.utils.aoa_to_sheet([groupHeaders, ...groupData]);
    
    ws3['!cols'] = [
      { wch: 30 },
      { wch: 15 },
      { wch: 80 },
      { wch: 15 }
    ];

    XLSX.utils.book_append_sheet(wb, ws3, 'Számlázási csoportok');
  }

  // Generate filename
  const fileName = `${customer.companyName || customer.name || 'Ügyfél'}_${new Date().toISOString().split('T')[0]}.xlsx`;

  // Write file
  XLSX.writeFile(wb, fileName);
};

/**
 * Export all customers to Excel (without PODs)
 */
export const exportAllCustomersToExcel = (customers: any[]) => {
  const wb = XLSX.utils.book_new();

  const headers = [
    'Ügyfél kód',
    'Cégnév',
    'Adószám',
    'Cégjegyzékszám',
    'Típus',
    'Státusz',
    'Email',
    'Telefon',
    'Kapcsolattartó név',
    'Kapcsolattartó email',
    'Kapcsolattartó telefon',
    'Cím - Ország',
    'Cím - Irányítószám',
    'Cím - Város',
    'Cím - Utca',
    'Számlázási email',
    'További számlázási email címek',
    'Fizetési mód',
    'Számlázási mód',
    'Pénznem',
    'Aktuális ár (Ft/kWh)',
    'Ár érvényesség kezdete',
    'Ár érvényesség vége',
    'Megjegyzés',
    'Létrehozva',
    'Frissítve'
  ];

  const data = customers.map(customer => [
    customer.customerCode || '',
    customer.companyName || `${customer.firstName || ''} ${customer.lastName || ''}`.trim(),
    customer.taxNumber || '',
    customer.registrationNumber || '',
    customer.type === 'organization' ? 'Szervezet' : 'Magánszemély',
    customer.status || '',
    customer.email || '',
    customer.phone || '',
    customer.contactPerson?.name || '',
    customer.contactPerson?.email || '',
    customer.contactPerson?.phone || '',
    customer.address?.country || '',
    customer.address?.postalCode || '',
    customer.address?.city || '',
    customer.address?.street || '',
    customer.billingEmail || '',
    (customer.additionalBillingEmails || []).join(', '),
    customer.paymentMethod || '',
    customer.bulkInvoicingSettings?.invoicingMode || '',
    customer.bulkInvoicingSettings?.currency || '',
    customer.currentPricing?.price || '',
    customer.currentPricing?.validFrom || '',
    customer.currentPricing?.validTo || '',
    customer.notes || '',
    customer.createdAt?.toDate?.()?.toLocaleDateString('hu-HU') || '',
    customer.updatedAt?.toDate?.()?.toLocaleDateString('hu-HU') || ''
  ]);

  const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);
  
  // Set column widths
  ws['!cols'] = [
    { wch: 12 }, // Ügyfél kód
    { wch: 35 }, // Cégnév
    { wch: 18 }, // Adószám
    { wch: 18 }, // Cégjegyzékszám
    { wch: 15 }, // Típus
    { wch: 12 }, // Státusz
    { wch: 30 }, // Email
    { wch: 15 }, // Telefon
    { wch: 25 }, // Kapcsolattartó név
    { wch: 30 }, // Kapcsolattartó email
    { wch: 15 }, // Kapcsolattartó telefon
    { wch: 15 }, // Ország
    { wch: 12 }, // Irányítószám
    { wch: 20 }, // Város
    { wch: 40 }, // Utca
    { wch: 30 }, // Számlázási email
    { wch: 15 }, // Fizetési mód
    { wch: 18 }, // Számlázási mód
    { wch: 10 }, // Pénznem
    { wch: 15 }, // Aktuális ár
    { wch: 15 }, // Ár kezdete
    { wch: 15 }, // Ár vége
    { wch: 40 }, // Megjegyzés
    { wch: 15 }, // Létrehozva
    { wch: 15 }  // Frissítve
  ];

  XLSX.utils.book_append_sheet(wb, ws, 'Ügyfelek');

  // Generate filename
  const fileName = `Ugyfelek_export_${new Date().toISOString().split('T')[0]}.xlsx`;
  
  // Write file
  XLSX.writeFile(wb, fileName);
};

/**
 * Export PODs to Excel
 */
export const exportPodsToExcel = (pods: any[], fileName?: string) => {
  const wb = XLSX.utils.book_new();

  const headers = [
    'POD Azonosító',
    'Ügyfél',
    'Cím',
    'Irányítószám',
    'Város',
    'Utca, házszám',
    'Típus',
    'Státusz',
    'Fogyasztási kategória',
    'Mérőóra típus',
    'Mérőóra szám',
    'Szerződés kezdete',
    'Szerződés vége',
    'Megjegyzés'
  ];

  const data = pods.map(pod => [
    pod.id || '',
    pod.customerName || '',
    pod.address || '',
    pod.postalCode || '',
    pod.city || '',
    pod.street || '',
    pod.type || '',
    pod.status || '',
    pod.consumptionCategory || '',
    pod.meterType || '',
    pod.meterNumber || '',
    pod.contractStartDate || '',
    pod.contractEndDate || '',
    pod.notes || ''
  ]);

  const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);
  
  // Set column widths
  ws['!cols'] = [
    { wch: 35 }, // POD ID
    { wch: 30 }, // Customer
    { wch: 40 }, // Address
    { wch: 15 }, // Postal Code
    { wch: 20 }, // City
    { wch: 30 }, // Street
    { wch: 15 }, // Type
    { wch: 15 }, // Status
    { wch: 20 }, // Category
    { wch: 20 }, // Meter type
    { wch: 20 }, // Meter number
    { wch: 15 }, // Start date
    { wch: 15 }, // End date
    { wch: 30 }  // Notes
  ];

  XLSX.utils.book_append_sheet(wb, ws, 'POD-ok');

  // Generate filename
  const defaultFileName = `POD_export_${new Date().toISOString().split('T')[0]}.xlsx`;
  
  // Write file
  XLSX.writeFile(wb, fileName || defaultFileName);
};
